import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const AboutPrivacy = () => {
  const [activeSection, setActiveSection] = useState('about');
  const location = useLocation();

  useEffect(() => {
    // Handle URL hash changes
    if (location.hash === '#privacy') {
      setActiveSection('privacy');
    } else {
      setActiveSection('about');
    }
    
    // Set up Intersection Observer for manual scrolling
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            if (sectionId === 'about' || sectionId === 'privacy') {
              setActiveSection(sectionId);
              // Update URL without triggering scroll
              if (window.location.hash !== `#${sectionId}`) {
                window.history.replaceState(null, null, `#${sectionId}`);
              }
            }
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: '-100px 0px -50% 0px'
      }
    );
    
    const aboutSection = document.getElementById('about');
    const privacySection = document.getElementById('privacy');
    
    if (aboutSection) observer.observe(aboutSection);
    if (privacySection) observer.observe(privacySection);
    
    return () => {
      if (aboutSection) observer.unobserve(aboutSection);
      if (privacySection) observer.unobserve(privacySection);
    };
  }, [location]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 100; // Adjust for navbar height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      
      // Update URL for bookmarking
      window.history.pushState(null, null, `#${sectionId}`);
      
      // Small delay to ensure smooth scroll completes
      setTimeout(() => {
        setActiveSection(sectionId);
      }, 300);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary-50 via-white to-primary-50">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-neutral-900 mb-6">
            About Omega Trust Bank
          </h1>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Learn more about our mission, values, and commitment to protecting your privacy.
          </p>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="sticky top-16 bg-white shadow-sm z-40 border-b border-neutral-200">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8">
            <button
              onClick={() => scrollToSection('about')}
              className={`py-4 px-2 font-semibold text-lg border-b-2 transition ${
                activeSection === 'about'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              About Us
            </button>
            <button
              onClick={() => scrollToSection('privacy')}
              className={`py-4 px-2 font-semibold text-lg border-b-2 transition ${
                activeSection === 'privacy'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      {/* About Us Section */}
      <section id="about" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Our Story & Mission
            </h2>
            <p className="text-xl text-neutral-600">
              Building the future of banking, one secure transaction at a time
            </p>
          </div>

          <div className="space-y-12">
            {/* Mission */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-neutral-100">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-600 to-accent-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-neutral-900">Our Mission</h3>
              </div>
              <p className="text-neutral-600 text-lg leading-relaxed">
                To revolutionize digital banking by providing secure, accessible, and innovative financial
                solutions that empower individuals and businesses to achieve their financial goals.
              </p>
            </div>

            {/* Values */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-neutral-100">
              <h3 className="text-2xl font-bold text-neutral-900 mb-6">Our Values</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    icon: '🔒',
                    title: 'Security First',
                    description: 'Your financial data is protected with bank-grade encryption and security protocols.'
                  },
                  {
                    icon: '🚀',
                    title: 'Innovation',
                    description: 'Constantly evolving to bring you the latest in financial technology and features.'
                  },
                  {
                    icon: '🤝',
                    title: 'Trust & Transparency',
                    description: 'We believe in building lasting relationships through honesty and clear communication.'
                  },
                  {
                    icon: '💡',
                    title: 'Simplicity',
                    description: 'Making complex banking operations simple and accessible for everyone.'
                  }
                ].map((value, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="text-2xl">{value.icon}</div>
                    <div>
                      <h4 className="font-bold text-neutral-900 mb-2">{value.title}</h4>
                      <p className="text-neutral-600">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Team Stats */}
            <div className="bg-primary-700 text-white p-8 rounded-2xl">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {[
                  { number: '50K+', label: 'Happy Customers' },
                  { number: '$2B+', label: 'Assets Managed' },
                  { number: '99.9%', label: 'System Uptime' },
                  { number: '24/7', label: 'Customer Support' }
                ].map((stat, index) => (
                  <div key={index}>
                    <div className="text-3xl md:text-4xl font-bold mb-2">{stat.number}</div>
                    <div className="text-primary-100">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section id="privacy" className="py-20 px-4 bg-neutral-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Privacy Policy
            </h2>
            <p className="text-xl text-neutral-600">
              How we protect and handle your personal information
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border border-neutral-100">
            <div className="space-y-8">
              {/* Information Collection */}
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Information We Collect
                </h3>
                <div className="space-y-3 text-neutral-600">
                  <p>We collect information that helps us provide and improve our services:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Personal identification information (name, email, phone number)</li>
                    <li>Financial information necessary for account operations</li>
                    <li>Transaction history and account activity</li>
                    <li>Technical data including IP addresses and device information</li>
                    <li>Usage data to improve our services</li>
                  </ul>
                </div>
              </div>

              {/* Data Protection */}
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  How We Protect Your Data
                </h3>
                <div className="space-y-3 text-neutral-600">
                  <p>We implement robust security measures to protect your information:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>256-bit SSL encryption for all data transmissions</li>
                    <li>Multi-factor authentication for account access</li>
                    <li>Regular security audits and penetration testing</li>
                    <li>Secure data storage with redundant backups</li>
                    <li>Employee training on data protection protocols</li>
                  </ul>
                </div>
              </div>

              {/* Data Usage */}
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  How We Use Your Information
                </h3>
                <div className="space-y-3 text-neutral-600">
                  <p>Your information helps us provide better services:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Process transactions and maintain your accounts</li>
                    <li>Provide customer support and respond to inquiries</li>
                    <li>Improve our platform and develop new features</li>
                    <li>Comply with legal and regulatory requirements</li>
                    <li>Send important updates about your accounts</li>
                  </ul>
                </div>
              </div>

              {/* Your Rights */}
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center">
                  <svg className="w-6 h-6 mr-3 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Your Privacy Rights
                </h3>
                <div className="space-y-3 text-neutral-600">
                  <p>You have control over your personal information:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Access and review your personal data</li>
                    <li>Request corrections to inaccurate information</li>
                    <li>Delete your account and associated data</li>
                    <li>Opt-out of marketing communications</li>
                    <li>Download your data in a portable format</li>
                  </ul>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-primary-50 p-6 rounded-xl border border-primary-200">
                <h4 className="font-bold text-primary-900 mb-2">Questions About Privacy?</h4>
                <p className="text-primary-700">
                  Contact our team at{' '}
                  <a href="mailto:privacy@omegatrustbank.com" className="underline hover:text-primary-900">
                    omegatrbnk01@aol.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Bank Better?
          </h2>
          <p className="text-xl mb-8 text-primary-100">
            Join thousands of users who trust us with their financial journey
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => window.location.href = '/register'}
              className="bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition transform hover:scale-105 shadow-lg text-lg"
            >
              Create Free Account
            </button>
            <button
              onClick={() => window.location.href = '/login'}
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition transform hover:scale-105 text-lg"
            >
              Sign In
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPrivacy;
